import { ChevronRightIcon } from '@langchain/untitled-ui-icons';
import { Button } from '@mui/joy';

import * as React from 'react';

import { GraphDatasetEditModal } from '@/Pages/Graph/src/components/debug/dataset/GraphDatasetEditModal';
import { DatasetPickerV2 } from '@/components/DatasetPickerV2';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { useDatasets } from '@/hooks/useSwr';
import SimpleEditIcon from '@/icons/SimpleEditIcon.svg?react';
import { isUUID } from '@/utils/uuid';

import { TraceLog } from '../../../data/misc';
import { GraphDatasetContext } from './GraphDatasetProvider';
import { getValidationErrors } from './GraphDatasetProvider.utils';

export const GraphDatasetInlineControls = (props: { traceLog: TraceLog }) => {
  const datasetState = React.useContext(GraphDatasetContext);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const datasetNode = datasetState.nodes?.find(
    (node) => node.id === props.traceLog.meta.id
  );

  const datasetPickerValue = datasetNode?.datasetNameOrId;
  const datasets = useDatasets(
    datasetPickerValue != null
      ? isUUID(datasetPickerValue)
        ? { id: [datasetPickerValue] }
        : { name: datasetPickerValue }
      : null,
    undefined,
    { revalidateOnFocus: false }
  );

  // Find the matching dataset from the datasets list
  const matchingDataset = datasets.data?.find(
    (dataset) =>
      dataset.id === datasetNode?.datasetNameOrId ||
      dataset.name === datasetNode?.datasetNameOrId
  );

  const hasSchemaError = React.useMemo(() => {
    // If no dataset is selected or no schema is defined, there can't be a mismatch
    if (
      !matchingDataset?.inputs_schema_definition &&
      !matchingDataset?.outputs_schema_definition
    ) {
      return false;
    }

    // Validate inputs schema if it exists
    if (
      getValidationErrors(
        matchingDataset.inputs_schema_definition,
        datasetNode?.stateInput
      )
    ) {
      return true;
    }

    // Validate outputs schema if it exists
    if (
      getValidationErrors(
        matchingDataset.outputs_schema_definition,
        datasetNode?.stateUpdate
      )
    ) {
      return true;
    }

    return false;
  }, [datasetNode, matchingDataset]);

  return (
    <>
      {datasetNode?.status?.type === 'error' && (
        <ExpandableErrorAlert error={datasetNode.status.error} />
      )}
      {datasetNode?.visible && (
        <div className="flex items-center gap-2">
          <Button
            size={'sm'}
            variant="outlined"
            color="neutral"
            startDecorator={
              <SimpleEditIcon className="h-4 w-4 stroke-current" />
            }
            onClick={() => setIsDialogOpen(true)}
          >
            Edit
          </Button>
          <DatasetPickerV2
            size="sm"
            value={datasetPickerValue}
            onChange={(newDatasetId) => {
              if (!newDatasetId || !datasetNode) return;
              datasetState.updateNode({
                ...datasetNode,
                datasetNameOrId: newDatasetId,
              });
            }}
          />

          {datasetNode?.datasetNameOrId &&
            !datasets.data?.some(
              (dataset) =>
                dataset.id === datasetNode?.datasetNameOrId ||
                dataset.name === datasetNode?.datasetNameOrId
            ) && (
              <div className="flex-shrink-0 rounded-full bg-[rgba(var(--joy-palette-success-mainChannel)/0.1)] px-3 py-1 text-xs font-semibold text-[rgba(var(--joy-palette-success-mainChannel)/1)]">
                New Dataset
              </div>
            )}

          {hasSchemaError && (
            <div className="flex flex-shrink-0 items-center gap-2 rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700">
              Schema mismatch
              <button
                type="button"
                onClick={() => setIsDialogOpen(true)}
                className="flex items-center gap-1 rounded-full bg-white px-2 py-0.5 font-medium transition-colors"
              >
                Fix issue
                <ChevronRightIcon className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}

      {isDialogOpen && datasetNode && (
        <GraphDatasetEditModal
          isOpen
          node={datasetNode}
          onClose={() => setIsDialogOpen(false)}
          updateNodes={datasetState.updateNode}
        />
      )}
    </>
  );
};
